import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Palette, PlusCircle, Bookmark, LogOut, Shield, User as UserIcon, Search, MessageCircle, MessagesSquare, Sun, Moon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [navQuery, setNavQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [dark, setDark] = useState(() => typeof document !== "undefined" && document.documentElement.classList.contains("dark"));
  const boxRef = useRef(null);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    try { localStorage.setItem("theme", next ? "dark" : "light"); } catch (e) {}
  };

  // poll unread message count
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const fetchUnread = () => api.get("/conversations").then(({ data }) => setUnread(data.total_unread || 0)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 10000);
    return () => clearInterval(t);
  }, [user]);

  // debounced live artist search
  useEffect(() => {
    const q = navQuery.trim();
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/users/search", { params: { q } });
      setResults(data);
      setOpen(true);
    }, 300);
    return () => clearTimeout(t);
  }, [navQuery]);

  // close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const gotoGallery = (q) => {
    setOpen(false);
    navigate(q ? `/feed?q=${encodeURIComponent(q)}` : "/feed");
  };

  const onSearch = (e) => {
    e.preventDefault();
    gotoGallery(navQuery.trim());
  };

  const gotoProfile = (id) => {
    setOpen(false);
    setNavQuery("");
    navigate(`/profile/${id}`);
  };

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      data-testid={`nav-link-${to.replace("/", "") || "home"}`}
      className={`text-sm transition-colors hover:text-primary ${pathname === to ? "text-primary" : "text-foreground/70"}`}
    >
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-primary text-primary-foreground">
            <Palette size={18} />
          </span>
          <span className="font-display text-xl tracking-tight">Solace</span>
        </Link>

        <form onSubmit={onSearch} ref={boxRef} className="hidden md:block flex-1 max-w-sm mx-8 relative">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="nav-search-input"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onFocus={() => { if (results.length) setOpen(true); }}
              placeholder="Search works or artists…"
              className="w-full rounded-full border border-border bg-secondary/40 pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-ring focus:bg-card transition-colors"
            />
          </div>

          {open && navQuery.trim() && (
            <div data-testid="nav-search-dropdown" className="absolute left-0 right-0 mt-2 rounded-2xl border border-border bg-popover shadow-[0_12px_40px_rgba(43,40,37,0.12)] overflow-hidden z-50">
              {results.length > 0 ? (
                <div className="py-2">
                  <p className="px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Artists</p>
                  {results.slice(0, 5).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`nav-search-artist-${a.id}`}
                      onClick={() => gotoProfile(a.id)}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-secondary/60 transition-colors text-left"
                    >
                      <span className="grid place-items-center w-8 h-8 rounded-full bg-secondary overflow-hidden text-xs shrink-0">
                        {a.avatar ? <img src={a.avatar} alt="" className="w-full h-full object-cover" /> : a.name?.[0]?.toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm truncate">{a.name}</span>
                        <span className="block text-xs text-muted-foreground">{a.artwork_count} works</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-3 text-sm text-muted-foreground">No artists found</p>
              )}
              <button
                type="button"
                data-testid="nav-search-all"
                onClick={() => gotoGallery(navQuery.trim())}
                className="w-full flex items-center gap-2 px-4 py-3 border-t border-border text-sm text-primary hover:bg-secondary/60 transition-colors"
              >
                <Search size={14} /> Search works for “{navQuery.trim()}”
              </button>
            </div>
          )}
        </form>

        <nav className="flex items-center gap-3 sm:gap-6">
          <NavLink to="/feed">Gallery</NavLink>
          <NavLink to="/discuss">Commons</NavLink>
          {user && <NavLink to="/collections">Saved</NavLink>}

          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
            className="grid place-items-center w-9 h-9 rounded-full border border-border text-foreground/70 hover:text-primary hover:border-primary/50 transition-colors"
          >
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          {user ? (
            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/messages"
                data-testid="nav-messages-btn"
                className="relative grid place-items-center w-9 h-9 rounded-full border border-border text-foreground/70 hover:text-primary hover:border-primary/50 transition-colors"
                title="Messages"
              >
                <MessageCircle size={18} />
                {unread > 0 && (
                  <span data-testid="nav-unread-badge" className="absolute -top-1 -right-1 grid place-items-center min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px]">
                    {unread}
                  </span>
                )}
              </Link>
              <Link
                to="/create"
                data-testid="nav-create-btn"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-3 sm:px-4 py-2 text-sm hover:-translate-y-0.5 transition-transform"
              >
                <PlusCircle size={16} /> <span className="hidden sm:inline">Share</span>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="nav-user-menu" className="outline-none">
                  <Avatar className="w-9 h-9 border border-border">
                    {user.avatar && <AvatarImage src={user.avatar} />}
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/profile/${user.id}`)}>
                    <UserIcon size={16} className="mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-collections" onClick={() => navigate("/collections")}>
                    <Bookmark size={16} className="mr-2" /> Saved
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <Shield size={16} className="mr-2" /> Moderation
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={onLogout}>
                    <LogOut size={16} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-signin-btn"
              className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm hover:-translate-y-0.5 transition-transform"
            >
              Enter Studio
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
